import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getFontStyles, getFontWeights } from "@repo/api/utils/font";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";

type FontItem = Awaited<ReturnType<typeof AdminApi.listFonts>>["fonts"][number];
type FontVariant = FontItem["metadata"]["variants"][number];

type SelectOpts = { weight?: number; style?: string };

export default function Fonts() {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<"custom" | "google">("custom");

  const [teamExternalId, setTeamExternalId] = useState("");
  const [family, setFamily] = useState("");
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  // Google Fonts import state
  const [googleFamily, setGoogleFamily] = useState("");
  const [googleText, setGoogleText] = useState("");
  const [seedLimit, setSeedLimit] = useState(10);

  // Preview selection state
  const [selectedFontId, setSelectedFontId] = useState<string | null>(null);
  const [selectedWeight, setSelectedWeight] = useState<number | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [previewFamily, setPreviewFamily] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState(
    "The quick brown fox jumps over the lazy dog",
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["adminFonts"],
    queryFn: () => AdminApi.listFonts(),
    retry: false,
  });

  const uploadMutation = useMutation({
    mutationFn: () =>
      AdminApi.uploadCustomFont({
        teamExternalId: teamExternalId.trim(),
        family: family.trim(),
        text: text.trim() || undefined,
        files,
      }),
    onSuccess: () => {
      toast.success("Font uploaded");
      setFamily("");
      setText("");
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: ["adminFonts"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to upload font");
    },
  });

  const googleMutation = useMutation({
    mutationFn: () =>
      AdminApi.createGoogleFonts([
        {
          family: googleFamily.trim(),
          text: googleText.trim() || undefined,
        },
      ]),
    onSuccess: (result) => {
      toast.success(
        result.created > 0 ? "Font imported" : "Font was already imported",
      );
      setGoogleFamily("");
      setGoogleText("");
      queryClient.invalidateQueries({ queryKey: ["adminFonts"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to import font");
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => AdminApi.seedGoogleFonts(seedLimit),
    onSuccess: (result) => {
      const seededText = `Seeded ${result.created} font${result.created !== 1 ? "s" : ""}`;
      toast.success(
        result.remaining > 0
          ? `${seededText} — ${result.remaining} remaining, run again to continue`
          : result.created > 0
            ? `${seededText} — all done`
            : "All fonts were already seeded",
      );
      queryClient.invalidateQueries({ queryKey: ["adminFonts"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to seed fonts");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (font: FontItem) =>
      AdminApi.deleteFont({
        teamExternalId: font.teamExternalId!,
        fontExternalId: font.externalId,
      }),
    onSuccess: (_data, font) => {
      toast.success("Font deleted");
      if (selectedFontId === font.externalId) {
        setSelectedFontId(null);
        setSelectedWeight(null);
        setSelectedStyle(null);
      }
      queryClient.invalidateQueries({ queryKey: ["adminFonts"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete font");
    },
  });

  const handleDeleteFont = (font: FontItem) => {
    if (!font.teamExternalId) return;
    if (
      !window.confirm(
        `Delete the font "${font.family}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(font);
  };

  // Team fonts first, then global fonts; order is otherwise preserved.
  const fonts = useMemo(() => {
    const list = data?.fonts ?? [];
    return [...list].sort((a, b) => {
      const aTeam = a.teamExternalId !== null ? 0 : 1;
      const bTeam = b.teamExternalId !== null ? 0 : 1;
      return aTeam - bTeam;
    });
  }, [data?.fonts]);

  const selectedFont =
    fonts.find((font) => font.externalId === selectedFontId) ?? null;

  /**
   * Select a font/weight/style ensuring a valid combination:
   * weight falls back to the first weight, style falls back to a style that
   * actually exists for the chosen weight.
   */
  const selectVariant = (font: FontItem, opts: SelectOpts) => {
    const isSame = font.externalId === selectedFontId;
    const weights = getFontWeights(font.metadata);

    let weight = opts.weight ?? (isSame ? selectedWeight : null);
    if (weight == null || !weights.includes(weight)) {
      weight = weights[0]!;
    }

    const stylePool = getFontStyles(font.metadata, weight);
    let style = opts.style ?? (isSame ? selectedStyle : null);
    if (!style || !stylePool.includes(style as FontVariant["style"])) {
      style = stylePool[0] ?? getFontStyles(font.metadata)[0]!;
    }

    setSelectedFontId(font.externalId);
    setSelectedWeight(weight);
    setSelectedStyle(style);
  };

  // Resolve the exact (or closest) variant for the chosen weight + style.
  const resolvedVariant = useMemo(() => {
    if (!selectedFont) return null;
    const variants = selectedFont.metadata.variants;
    if (variants.length === 0) return null;

    const exact = variants.find(
      (v) => v.weight === selectedWeight && v.style === selectedStyle,
    );
    if (exact) return exact;

    const weight = selectedWeight ?? getFontWeights(selectedFont.metadata)[0]!;
    const style = selectedStyle ?? getFontStyles(selectedFont.metadata)[0];
    const sameStyle = variants.filter((v) => v.style === style);
    const pool = sameStyle.length > 0 ? sameStyle : variants;

    return pool.reduce((best, v) =>
      Math.abs(v.weight - weight) < Math.abs(best.weight - weight) ? v : best,
    );
  }, [selectedFont, selectedWeight, selectedStyle]);

  // Load the resolved font file into the document so we can render with it.
  useEffect(() => {
    if (!selectedFont || !resolvedVariant) {
      setPreviewFamily(null);
      return;
    }

    const familyName = `amplifiles-preview-${selectedFont.externalId}-${resolvedVariant.weight}-${resolvedVariant.style}`;
    let cancelled = false;

    const fontFace = new FontFace(familyName, `url(${resolvedVariant.url})`, {
      weight: String(resolvedVariant.weight),
      style: resolvedVariant.style,
    });

    fontFace
      .load()
      .then((loaded) => {
        if (cancelled) return;
        document.fonts.add(loaded);
        setPreviewFamily(familyName);
      })
      .catch(() => {
        if (!cancelled) setPreviewFamily(null);
      });

    return () => {
      cancelled = true;
      document.fonts.delete(fontFace);
    };
  }, [
    selectedFont,
    resolvedVariant?.url,
    resolvedVariant?.weight,
    resolvedVariant?.style,
  ]);

  const canSubmit =
    teamExternalId.trim().length > 0 &&
    family.trim().length > 0 &&
    files.length > 0 &&
    !uploadMutation.isPending;

  const canSubmitGoogle =
    googleFamily.trim().length > 0 && !googleMutation.isPending;

  return (
    <AdminLayout
      title="Fonts"
      description="Upload custom font families and view all stored font previews"
      breadcrumb="Fonts"
    >
      <div className="max-w-5xl mx-auto">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">
              {error instanceof Error ? error.message : "Failed to load fonts"}
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab("custom")}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === "custom"
                  ? "text-gray-900 border-b-2 border-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Custom upload
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("google")}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === "google"
                  ? "text-gray-900 border-b-2 border-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Google Fonts
            </button>
          </div>

          {activeTab === "custom" ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (!canSubmit) return;
                uploadMutation.mutate();
              }}
              className="p-6"
            >
              <h3 className="font-semibold text-gray-900 mb-1">
                Upload custom font
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Upload one font family at a time. Weight and style are derived
                from each file. Accepts WOFF2, WOFF, TTF, and OTF.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team external ID
                  </label>
                  <input
                    type="text"
                    value={teamExternalId}
                    onChange={(e) => setTeamExternalId(e.target.value)}
                    placeholder="team_..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Font family name
                  </label>
                  <input
                    type="text"
                    value={family}
                    onChange={(e) => setFamily(e.target.value)}
                    placeholder="My Custom Font"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preview text (optional)
                  </label>
                  <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Defaults to the family name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Font files
                  </label>
                  <input
                    type="file"
                    multiple
                    accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
                    onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                    className="w-full text-sm text-gray-700"
                  />
                  {files.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {files.length} file{files.length !== 1 ? "s" : ""}{" "}
                      selected
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
                >
                  {uploadMutation.isPending ? "Uploading..." : "Upload font"}
                </button>
              </div>
            </form>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (!canSubmitGoogle) return;
                googleMutation.mutate();
              }}
              className="p-6"
            >
              <h3 className="font-semibold text-gray-900 mb-1">
                Import from Google Fonts
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Import a font family by name. All available weights and styles
                are downloaded and stored.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Font family name
                  </label>
                  <input
                    type="text"
                    value={googleFamily}
                    onChange={(e) => setGoogleFamily(e.target.value)}
                    placeholder="Inter"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preview text (optional)
                  </label>
                  <input
                    type="text"
                    value={googleText}
                    onChange={(e) => setGoogleText(e.target.value)}
                    placeholder="Defaults to the family name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  disabled={!canSubmitGoogle}
                  className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
                >
                  {googleMutation.isPending ? "Importing..." : "Import font"}
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">
                  Or seed built-in font families in batches. Run again to seed
                  the next batch.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={seedLimit}
                    onChange={(e) =>
                      setSeedLimit(
                        Math.min(50, Math.max(1, Number(e.target.value) || 1)),
                      )
                    }
                    className="w-20 border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => seedMutation.mutate()}
                    disabled={seedMutation.isPending}
                    className="bg-white text-gray-900 text-sm font-medium px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {seedMutation.isPending ? "Seeding..." : "Seed fonts"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        <FontPreview
          fonts={fonts}
          selectedFont={selectedFont}
          selectedWeight={selectedWeight}
          selectedStyle={selectedStyle}
          resolvedVariant={resolvedVariant}
          previewFamily={previewFamily}
          previewText={previewText}
          onPreviewTextChange={setPreviewText}
          onSelect={selectVariant}
        />

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Uploaded fonts</h3>
            <p className="text-sm text-gray-500 mt-1">
              {fonts.length} font{fonts.length !== 1 ? "s" : ""}
            </p>
          </div>

          {isLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">
              Loading...
            </div>
          ) : fonts.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">No fonts.</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {fonts.map((font) => (
                <FontListItem
                  key={font.externalId}
                  font={font}
                  isSelected={selectedFontId === font.externalId}
                  selectedWeight={selectedWeight}
                  selectedStyle={selectedStyle}
                  onSelect={selectVariant}
                  onDelete={handleDeleteFont}
                  isDeleting={
                    deleteMutation.isPending &&
                    deleteMutation.variables?.externalId === font.externalId
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function FontPreview({
  fonts,
  selectedFont,
  selectedWeight,
  selectedStyle,
  resolvedVariant,
  previewFamily,
  previewText,
  onPreviewTextChange,
  onSelect,
}: {
  fonts: FontItem[];
  selectedFont: FontItem | null;
  selectedWeight: number | null;
  selectedStyle: string | null;
  resolvedVariant: FontVariant | null;
  previewFamily: string | null;
  previewText: string;
  onPreviewTextChange: (value: string) => void;
  onSelect: (font: FontItem, opts: SelectOpts) => void;
}) {
  const styleOptions = selectedFont
    ? getFontStyles(selectedFont.metadata, selectedWeight)
    : [];

  const selectClassName =
    "border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50";

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {/* Font family */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Font family
          </label>
          <select
            value={selectedFont?.externalId ?? ""}
            onChange={(e) => {
              const font = fonts.find((f) => f.externalId === e.target.value);
              if (font) onSelect(font, {});
            }}
            className={`w-full ${selectClassName}`}
          >
            <option value="" disabled>
              Select font family
            </option>
            {fonts.map((font) => (
              <option key={font.externalId} value={font.externalId}>
                {font.family}
              </option>
            ))}
          </select>
        </div>

        {/* Weight (depends on family) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Weight
          </label>
          <select
            value={selectedWeight ?? ""}
            disabled={!selectedFont}
            onChange={(e) =>
              selectedFont &&
              onSelect(selectedFont, { weight: Number(e.target.value) })
            }
            className={`w-full ${selectClassName}`}
          >
            <option value="" disabled>
              Weight
            </option>
            {selectedFont &&
              getFontWeights(selectedFont.metadata).map((weight) => (
                <option key={weight} value={weight}>
                  {weight}
                </option>
              ))}
          </select>
        </div>

        {/* Style (depends on weight) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Style
          </label>
          <select
            value={selectedStyle ?? ""}
            disabled={!selectedFont}
            onChange={(e) =>
              selectedFont && onSelect(selectedFont, { style: e.target.value })
            }
            className={`w-full ${selectClassName}`}
          >
            <option value="" disabled>
              Style
            </option>
            {styleOptions.map((style) => (
              <option key={style} value={style}>
                {style}
              </option>
            ))}
          </select>
        </div>
      </div>

      <input
        type="text"
        value={previewText}
        onChange={(e) => onPreviewTextChange(e.target.value)}
        placeholder="Type preview text"
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4"
      />

      <p
        className="text-4xl text-gray-900 leading-tight break-words min-h-[3rem]"
        style={{
          fontFamily: previewFamily ?? "inherit",
          fontWeight: resolvedVariant?.weight,
          fontStyle: resolvedVariant?.style,
        }}
      >
        {previewText || "The quick brown fox jumps over the lazy dog"}
      </p>
    </div>
  );
}

function FontListItem({
  font,
  isSelected,
  selectedWeight,
  selectedStyle,
  onSelect,
  onDelete,
  isDeleting,
}: {
  font: FontItem;
  isSelected: boolean;
  selectedWeight: number | null;
  selectedStyle: string | null;
  onSelect: (font: FontItem, opts: SelectOpts) => void;
  onDelete: (font: FontItem) => void;
  isDeleting: boolean;
}) {
  // Global fonts (no team) cannot be deleted.
  const canDelete = font.teamExternalId !== null;
  const weights = getFontWeights(font.metadata);
  // Base style availability on the active weight (selected weight when this
  // row is selected, otherwise the font's first weight as a fallback).
  const effectiveWeight = isSelected ? selectedWeight : (weights[0] ?? null);
  const availableStyles = getFontStyles(font.metadata, effectiveWeight);

  return (
    <li className="px-6 py-4 flex items-center gap-4">
      <img
        src={font.metadata.previewSrc}
        alt={font.family}
        className="h-16 w-auto max-w-xs object-contain bg-gray-50 rounded border border-gray-100"
      />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => onSelect(font, {})}
          className={`font-medium truncate text-left ${
            isSelected ? "text-blue-600" : "text-gray-900"
          }`}
        >
          {font.family}
        </button>
        <p className="text-xs text-gray-400 mb-1">
          {font.teamExternalId === null
            ? "Global"
            : `Team ${font.teamExternalId}`}
        </p>
        <div className="flex flex-wrap items-center gap-1">
          {weights.map((weight) => {
            const active = isSelected && selectedWeight === weight;
            return (
              <button
                key={`w-${weight}`}
                type="button"
                onClick={() => onSelect(font, { weight })}
                className={`text-xs px-2 py-0.5 rounded border ${
                  active
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                }`}
              >
                {weight}
              </button>
            );
          })}

          <span className="mx-1 text-gray-300">·</span>

          {getFontStyles(font.metadata).map((style) => {
            const available = availableStyles.includes(style);
            const active = isSelected && selectedStyle === style;
            return (
              <button
                key={`s-${style}`}
                type="button"
                disabled={!available}
                onClick={() => onSelect(font, { style })}
                className={`text-xs px-2 py-0.5 rounded border ${
                  active
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50`}
              >
                {style}
              </button>
            );
          })}
        </div>
      </div>

      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(font)}
          disabled={isDeleting}
          className="shrink-0 text-xs font-medium text-red-600 hover:text-red-700 border border-red-200 hover:bg-red-50 rounded-md px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>
      )}
    </li>
  );
}
